import { useMemo } from 'react';
import { ThemeInitializer } from '@sphere/shared/ui/design-system';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { router } from './router';
import { KeycloakProvider } from './KeycloakProvider';
import { BootstrapLoader } from './BootstrapLoader';
import { ErrorBoundary } from '@sphere/shared/ui/design-system';
import { Toaster } from 'sonner';

/**
 * Configuration améliorée du QueryClient
 */
function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60_000, // 60 secondes
        gcTime: 5 * 60_000, // 5 minutes (remplace cacheTime)
        refetchOnWindowFocus: false,
        refetchOnMount: true, // Refetch des données périmées au montage (évite données obsolètes)
        refetchOnReconnect: true, // ✅ Amélioration: Re-fetch au retour de connexion
        retry: (failureCount, error: any) => {
          // ✅ Amélioration: Retry logic intelligent
          const status = error?.response?.status;

          // Ne pas retry sur les erreurs client (4xx sauf 408, 429)
          if (status && status >= 400 && status < 500) {
            if (status === 408 || status === 429) {
              return failureCount < 2;
            }
            return false;
          }

          // Retry jusqu'à 3 fois pour les erreurs serveur
          return failureCount < 3;
        },
        retryDelay: (attemptIndex) => {
          // ✅ Amélioration: Backoff exponentiel
          return Math.min(1000 * 2 ** attemptIndex, 30000);
        },
      },
      mutations: {
        retry: false, // ✅ Ne pas retry les mutations par défaut
        // ✅ Amélioration: Gestion globale des erreurs de mutation
        onError: (error: any) => {
          console.error('Mutation error:', error);
          // Ici, on pourrait ajouter un toast d'erreur global
        },
      },
    },
  });
}

/**
 * Provider racine de l'application avec hiérarchie optimisée
 */
export function AppProviders() {
  // ✅ Amélioration: Créer le queryClient une seule fois
  const queryClient = useMemo(() => createQueryClient(), []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeInitializer />
        <Toaster position="top-right" richColors />

        <KeycloakProvider landingPath="/fr_fr">
          <BootstrapLoader>
            <RouterProvider router={router} />
          </BootstrapLoader>
        </KeycloakProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
